import { useState } from 'react';
import { TextInputRaw } from '../../components/Input/TextInputRaw';
import { Switch } from '../../components/common/Switch';
import { Button } from '../../components/common/Button';
import { sendCustomWebhookApi, updateWorkspaceApi } from '../../helper/api';
import { useTrackActivity } from '../../hooks/track';

// using this webhook will post to #random channel in Zync Slack
export const TEST_WEBHOOK_URL =
  'https://hooks.zapier.com/hooks/catch/16260304/2nsuhcg/';

const WEBHOOK_TEST_DATA = {
  contentKit: {
    video: {
      square: 'https://cdn.zync.ai/media/video/video-1726848119767.mp4',
      portrait: 'https://cdn.zync.ai/media/video/video-1726848449778.mp4',
      landscape: 'https://cdn.zync.ai/media/video/video-1726848267501.mp4',
    },
    thumbnail: {
      square:
        'https://cdn.zync.ai/media/content/HN6p1wfM/image_1726848070600_63.png',
      portrait:
        'https://cdn.zync.ai/media/content/HN6p1wfM/image_1726848082914_QW.png',
      landscape:
        'https://cdn.zync.ai/media/content/HN6p1wfM/image_1726848085038_4S.png',
    },
    webpageUrl: `https://app.zync.ai/videopage/overcoming-fear-of-creating-video-content`,
    transcriptText:
      "One of the common things that I hear when I ask people why they don't post in a video content on socials, they say that I'm not a video person. I don't have the face for video or some variations of, my accent or the words that I use, and I'm exactly the same. Many days, I get up and say that, like, I don't look good enough for video or I shouldn't be doing video. And then I realized that this is all stemming from this fear or vulnerability of putting ourselves out there. I have to tell myself every day that are you going to be the kind of person who's gonna let that fear and vulnerability keep you from achieving what you want to achieve? And that the way that I ask that question to myself makes it very evident in what I want to do next. Tell me how this works for you.",
  },
};

export const isValidUrl = (url) => {
  try {
    const parsed = new URL(url);

    return parsed.protocol === 'https:' && !/\s/.test(url);
  } catch {
    return false;
  }
};

export const WebhookIntegrations = ({ workspace, onWorkspaceUpdated }) => {
  const track = useTrackActivity();
  const { integrations, workspaceId } = workspace || {};
  const { webhook } = integrations || {};
  const { webhookUrl = '', autoPublishToWebhook = false } = webhook || {};

  const [webhookChanges, setWebhookChanges] = useState({
    webhookUrl,
    autoPublishToWebhook,
  });
  const [isUpdateWorkspaceLoading, setIsUpdateWorkspaceLoading] =
    useState(false);
  const [isDeleteWorkspaceLoading, setIsDeleteWorkspaceLoading] =
    useState(false);
  const [isSendTestDataLoading, setIsSendTestDataLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const hasWebookChanged = Boolean(
    [
      [webhookChanges.webhookUrl, webhookUrl],
      [webhookChanges.autoPublishToWebhook, autoPublishToWebhook],
    ].find(([value, newValue]) => value !== newValue)
  );

  const isValidWebhookUrl = isValidUrl(webhookChanges.webhookUrl);

  const handleDelete = async () => {
    setError(null);
    setSuccess(null);
    setIsDeleteWorkspaceLoading(true);

    track('[Webhook Integration]: Delete Clicked');

    const { error: updateError, result } = await updateWorkspaceApi(
      workspaceId,
      { webhook: { webhookUrl: '', autoPublishToWebhook: false } }
    );

    if (updateError) {
      setError('There was an error deleting the webhook, please try again');
      setIsDeleteWorkspaceLoading(false);
      return;
    }

    setWebhookChanges({ webhookUrl: '', autoPublishToWebhook: false });

    if (onWorkspaceUpdated) {
      onWorkspaceUpdated(result.workspace);
    }

    setIsDeleteWorkspaceLoading(false);
  };

  const handleUpdate = async () => {
    setError(null);
    setSuccess(null);
    const trimmedWebhookUrl = webhookChanges.webhookUrl;

    if (!isValidUrl(trimmedWebhookUrl)) {
      setError('Enter a valid webhook url');
      return;
    }

    setIsUpdateWorkspaceLoading(true);

    track('[Webhook Integration]: Update Webhook Configuration Clicked');

    const { error: updateError, result } = await updateWorkspaceApi(
      workspaceId,
      { webhook: { ...webhookChanges } }
    );

    if (updateError) {
      setError('There was an error updating your changes, please try again');
      setIsUpdateWorkspaceLoading(false);
      return;
    }

    if (onWorkspaceUpdated) {
      onWorkspaceUpdated(result.workspace);
    }

    setIsUpdateWorkspaceLoading(false);
  };

  const sendTestData = async () => {
    setError(null);
    setSuccess(null);
    setIsSendTestDataLoading(true);
    try {
      track('[Webhook Integration]: Sent Test Data');

      const webhookSuccess = await sendCustomWebhookApi({
        webhookUrl: webhookChanges.webhookUrl,
        data: WEBHOOK_TEST_DATA,
        workspaceId,
      });

      if (webhookSuccess) {
        setSuccess('Successful response from webhook');
      } else {
        setError('Error response from webhook');
        setIsSendTestDataLoading(false);
      }
    } catch (error) {
      setError('Error response from webhook');
      setIsSendTestDataLoading(false);
    }
  };

  return (
    <div className="max-w-[900px] mx-auto pb-6 mt-8">
      <div className="bg-white border border-[#EBEAED] rounded-lg mb-0">
        <h3 className="text-blue-dark font-semibold bg-purple/5 px-4 py-3">
          Webhook Configuration
        </h3>
        <div className="flex flex-col gap-4 p-5">
          <TextInputRaw
            label="Webhook URL"
            value={webhookChanges.webhookUrl}
            onChange={(event, value) => {
              setWebhookChanges((prev) => ({ ...prev, webhookUrl: value }));
            }}
          />
          <div className="flex gap-2 items-center">
            <div className="text-blue-dark font-medium text-sm font-inter flex gap-1">
              Auto Publish to Webhook
            </div>
            <Switch
              type={'Webhook Auto Publish'}
              checked={webhookChanges.autoPublishToWebhook}
              onChangeValue={(checked) => {
                setWebhookChanges((prev) => ({
                  ...prev,
                  autoPublishToWebhook: checked,
                }));
              }}
            />
          </div>
          <div className="flex gap-2 w-full">
            {webhookUrl?.length > 0 && (
              <Button
                color={Button.colors.RED}
                size={Button.sizes.FIT}
                state={
                  isUpdateWorkspaceLoading
                    ? Button.states.DISABLED
                    : isDeleteWorkspaceLoading
                    ? Button.states.LOADING
                    : Button.states.DEFAULT
                }
                onClick={() => {
                  handleDelete();
                }}
              >
                <span className="text-sm">Delete Webhook</span>
              </Button>
            )}
            <Button
              state={
                isValidWebhookUrl
                  ? isSendTestDataLoading
                    ? Button.states.LOADING
                    : Button.states.DEFAULT
                  : Button.states.DISABLED
              }
              color={Button.colors.PURPLE}
              size={Button.sizes.FIT}
              onClick={() => sendTestData()}
            >
              <span className="text-sm">Send Test Data</span>
            </Button>
            <Button
              state={
                hasWebookChanged || !isDeleteWorkspaceLoading
                  ? isUpdateWorkspaceLoading
                    ? Button.states.LOADING
                    : Button.states.DEFAULT
                  : Button.states.DISABLED
              }
              color={Button.colors.PURPLE}
              size={Button.sizes.FIT}
              onClick={() => handleUpdate()}
            >
              <span className="text-sm">Update Webhook Configuration</span>
            </Button>
          </div>
          {success ? (
            <span className="text-[#22c55e] font-medium text-xxs">
              {success}
            </span>
          ) : null}
          {error ? (
            <span className="text-red font-medium text-xxs">{error}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
};
